import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Divider,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RefreshIcon from '@mui/icons-material/Refresh';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import config from './config';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { formatInputCurrency, parseCurrency, formatCurrency } from './utils/currency';

const ImovelForm = ({
  onClose,
  calculatedArea,
  constructionArea,
  addressInput,
  selectedMunicipio,
  selectedCidade,
  selectedBairro,
  selectedEndereco,
  userData,
}) => {
  // Initialize state with saved data from localStorage or default values
  const [matricula, setMatricula] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    const matriculaFromSelection = localStorage.getItem('matricula');
    return saved ? JSON.parse(saved).matricula : matriculaFromSelection || '';
  });
  const [imovelData, setImovelData] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).imovelData : { cubc: [], vida_util: [], grau_cons: [], mercado: [] };
  });
  const [areaLote, setAreaLote] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).areaLote : calculatedArea || '';
  });
  const [areaConstruida, setAreaConstruida] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).areaConstruida : constructionArea || '';
  });
  const [idadeAparente, setIdadeAparente] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).idadeAparente : '';
  });
  const [valorInformado, setValorInformado] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).valorInformado : '';
  });
  const [selectedVidaUtil, setSelectedVidaUtil] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).selectedVidaUtil : '';
  });
  const [selectedGrauCons, setSelectedGrauCons] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).selectedGrauCons : '';
  });
  const [selectedCUBC, setSelectedCUBC] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).selectedCUBC : '';
  });
  const [selectedMercado, setSelectedMercado] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).selectedMercado : '';
  });
  const [clientes, setClientes] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).clientes : [{ nome: '', cpf: '', endereco: '', telefone: '' }];
  });
  const [calculationResult, setCalculationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentEndereco, setCurrentEndereco] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).currentEndereco : selectedEndereco ? '' : '';
  });
  const [enderecos, setEnderecos] = useState([]);
  const [openIssueModal, setOpenIssueModal] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');
  const [openObservationsModal, setOpenObservationsModal] = useState(false);
  const [observations, setObservations] = useState(() => {
    const saved = localStorage.getItem('imovelFormData');
    return saved ? JSON.parse(saved).observations || '' : '';
  });

  // Track whether fields have been manually edited
  const [userEdited, setUserEdited] = useState({
    areaLote: false,
    areaConstruida: false,
  });

  const [showAlert, setShowAlert] = useState(false);

  const selectedEnderecoObj = enderecos.find(
    (end) => end.ID_ENDERECO === currentEndereco
  );
  const metodo = selectedEnderecoObj ? selectedEnderecoObj.METODO : undefined;

  // Reset functions for each section
  const resetClientes = () => {
    setClientes([{ nome: '', cpf: '', endereco: '', telefone: '' }]);
    setCalculationResult(null);
    setError(null);
  };

  const resetRegiao = () => {
    setCurrentEndereco('');
    setValorInformado('');
    setCalculationResult(null);
    setError(null);
  };

  const resetImovel = () => {
    setAreaLote(calculatedArea || '');
    setAreaConstruida(constructionArea || '');
    setUserEdited({ areaLote: false, areaConstruida: false });
    setCalculationResult(null);
    setError(null);
  };

  const resetConstrucao = () => {
    setSelectedMercado('');
    setIdadeAparente('');
    setCalculationResult(null);
    setError(null);
    setSelectedVidaUtil('');
    setSelectedGrauCons('');
    setSelectedCUBC('');
  };

  const resetMatricula = () => {
    setMatricula('');
  };

  // Update areaLote and areaConstruida when props change, unless user has edited
  useEffect(() => {
    if (!userEdited.areaLote && calculatedArea !== undefined) {
      setAreaLote(calculatedArea || '');
    }
    if (!userEdited.areaConstruida && constructionArea !== undefined) {
      setAreaConstruida(constructionArea || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedArea, constructionArea]);

  // Save form data to localStorage whenever relevant state changes
  useEffect(() => {
    const formData = {
      imovelData,
      areaLote,
      areaConstruida,
      idadeAparente,
      valorInformado,
      selectedVidaUtil,
      selectedGrauCons,
      selectedCUBC,
      selectedMercado,
      clientes,
      currentEndereco,
      observations,
      matricula,
    };
    localStorage.setItem('imovelFormData', JSON.stringify(formData));
  }, [
    imovelData,
    areaLote,
    areaConstruida,
    idadeAparente,
    valorInformado,
    selectedVidaUtil,
    selectedGrauCons,
    selectedCUBC,
    selectedMercado,
    clientes,
    currentEndereco,
    observations,
    matricula,
  ]);

  // Filtrar cidades e bairros
  const filteredCidades = userData.cidades.filter(
    (cidade) => cidade.ID_MUNICIPIO === selectedMunicipio
  );
  const filteredBairros = userData.bairros.filter(
    (bairro) => bairro.ID_CIDADE === selectedCidade
  );

  // Função para abrir o modal de observações
  const handleOpenObservationsModal = () => {
    setOpenObservationsModal(true);
  };

  // Função para fechar o modal de observações
  const handleCloseObservationsModal = () => {
    setOpenObservationsModal(false);
  };

  // Função para salvar observações
  const handleSaveObservations = () => {
    setOpenObservationsModal(false);
  };

  // Função para abrir o modal de problema
  const handleOpenIssueModal = () => {
    setOpenIssueModal(true);
  };

  // Função para fechar o modal de problema
  const handleCloseIssueModal = () => {
    setOpenIssueModal(false);
    setIssueDescription('');
  };

  // Função para enviar o chamado
  const handleSubmitIssue = async () => {
    try {
      const token = localStorage.getItem('token');
      // Montar o endereço completo
      const municipioNome = userData.municipios.find(
        (mun) => mun.ID_MUNICIPIO === selectedMunicipio
      )?.NOME_MUNICIPIO;
      const cidadeNome = userData.cidades.find(
        (cidade) => cidade.ID_CIDADE === selectedCidade
      )?.NOME_CIDADE;
      const bairroNome = selectedBairro
        ? userData.bairros.find((bairro) => bairro.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO
        : '';
      const enderecoNome = enderecos.find(
        (end) => end.ID_ENDERECO === currentEndereco
      )?.NOME_ENDERECO;

      const fullAddress = `${enderecoNome || ''}, ${bairroNome ? bairroNome + ', ' : ''}${cidadeNome || ''}, ${municipioNome || ''}`;

      // Enviar o chamado para a API
      await axios.post(
        `${config.API_URL}/reportar-chamado`,
        {
          endereco: fullAddress,
          problema: issueDescription,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Fechar o modal após o envio
      handleCloseIssueModal();
      alert('Chamado enviado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar chamado:', error.response || error.message);
      setError('Erro ao enviar chamado: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Montar o endereço completo para exibir no modal
  const municipioNome = userData.municipios.find(
    (mun) => mun.ID_MUNICIPIO === selectedMunicipio
  )?.NOME_MUNICIPIO;
  const cidadeNome = userData.cidades.find(
    (cidade) => cidade.ID_CIDADE === selectedCidade
  )?.NOME_CIDADE;
  const bairroNome = selectedBairro
    ? userData.bairros.find((bairro) => bairro.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO
    : '';
  const enderecoNome = enderecos.find(
    (end) => end.ID_ENDERECO === currentEndereco
  )?.NOME_ENDERECO;

  const fullAddress = `${enderecoNome || ''}, ${bairroNome ? bairroNome + ', ' : ''}${cidadeNome || ''}, ${municipioNome || ''}`;

  // Carregar dados do endpoint /imovel-data
  useEffect(() => {
    const fetchImovelData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${config.API_URL}/imovel-data`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setImovelData(response.data);
      } catch (error) {
        console.error('Erro ao buscar dados do imóvel:', error.response || error.message);
        setError('Erro ao buscar dados do imóvel: ' + (error.response?.data?.detail || error.message));
      }
    };
    fetchImovelData();
  }, []);

  // Buscar endereços quando cidade ou bairro são fornecidos
  useEffect(() => {
    const fetchEnderecos = async () => {
      if (!selectedCidade) {
        setEnderecos([]);
        setCurrentEndereco('');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const cidadeNome = userData.cidades.find(
          (cidade) => cidade.ID_CIDADE === selectedCidade
        )?.NOME_CIDADE;
        const bairroNome = selectedBairro
          ? userData.bairros.find((bairro) => bairro.ID_BAIRRO === selectedBairro)?.NOME_BAIRRO
          : null;

        const response = await axios.post(
          `${config.API_URL}/regioes`,
          {
            cidade: cidadeNome,
            bairro: bairroNome,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const enderecos = response.data.filter(endereco => endereco.URBANO_RURAL === 1);
        setEnderecos(enderecos);

        if (selectedEndereco) {
          const enderecoMatch = response.data.find(
            (end) => end.NOME_ENDERECO === selectedEndereco
          );
          if (enderecoMatch) {
            setCurrentEndereco(enderecoMatch.ID_ENDERECO);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar regiões:', error.response || error.message);
        setError('Erro ao buscar regiões: ' + (error.response?.data?.detail || error.message));
        setEnderecos([]);
        setCurrentEndereco('');
      }
    };

    fetchEnderecos();
  }, [selectedCidade, selectedBairro, userData.cidades, userData.bairros, selectedEndereco]);

  // Função para adicionar um novo cliente
  const handleAddCliente = () => {
    setClientes([...clientes, { nome: '', cpf: '', endereco: '', telefone: '' }]);
  };

  // Função para remover um cliente
  const handleRemoveCliente = (index) => {
    if (clientes.length > 1) {
      setClientes(clientes.filter((_, i) => i !== index));
    }
  };

  // Função para atualizar os dados de um cliente
  const handleClienteChange = (index, field, value) => {
    const updatedClientes = [...clientes];
    updatedClientes[index][field] = value;
    setClientes(updatedClientes);
  };

  // Função para validar os campos
  const validateFields = () => {
    const newErrors = {
      areaLote: !areaLote,
      currentEndereco: !currentEndereco,
      valorInformado: !valorInformado || parseFloat(valorInformado) < 0,
      matricula: !matricula,
    };
    setErrors(newErrors);

    // Retorna true se todos os campos forem válidos, false caso contrário
    return !Object.values(newErrors).some((error) => error);
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Função para calcular os valores
  const handleCalculate = async () => {
    if (validateFields()) {
      setLoading(true);
      setError(null);
      setCalculationResult(null);
      await sleep(500);

      const savedPolygons = localStorage.getItem('mapPolygons');
      const savedAreas = localStorage.getItem('mapAreas');

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${config.API_URL}/calcular-benfeitoria`,
          {
            id_municipio: selectedMunicipio || null,
            id_cidade: selectedCidade || null,
            id_bairro: selectedBairro || null,
            id_endereco: currentEndereco || null,
            area_lote: parseFloat(areaLote) || null,
            area_construida: parseFloat(areaConstruida) || null,
            idade_aparente: parseInt(idadeAparente) || null,
            valor_informado: parseCurrency(valorInformado) || null,
            id_vida_util: selectedVidaUtil || null,
            id_grau_cons: selectedGrauCons || null,
            id_cubc: selectedCUBC || null,
            id_mercado: parseInt(selectedMercado) || null,
            clientes: clientes,
            observacoes: observations,
            urbano_rural: 1,
            matricula,
            mapPolygons: savedPolygons ? JSON.parse(savedPolygons) : null,
            mapAreas: savedAreas ? JSON.parse(savedAreas) : null
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCalculationResult(response.data);
        // Optionally clear localStorage after successful calculation
        // localStorage.removeItem('imovelFormData');
      } catch (error) {
        console.error('Erro ao calcular benfeitoria:', error.response || error.message);
        setError('Erro ao calcular benfeitoria: ' + (error.response?.data?.detail || error.message));
      } finally {
        setLoading(false);
      }
      setShowAlert(false);
      setErrors({
        areaLote: false,
        currentEndereco: false,
        valorInformado: false,
        matricula: false,
        idadeAparente: false,
      });
    } else {
      setShowAlert(true); // Mostrar o alerta se houver campos inválidos
    }
  };

  // Função para determinar a cor do valor informado
  const getValorInformadoColor = (valor, inferior, superior) => {
    if (valor === null || valor === undefined) return 'text.primary';
    if (valor < inferior || valor > superior) return 'error.main';
    return 'success.main';
  };

  const getValorInformadoIcon = (valor, inferior, superior) => {
    if (valor === null || valor === undefined) return null;
    if (valor < inferior) return <ArrowDownwardIcon fontSize="small" />;
    if (valor > superior) return <ArrowUpwardIcon fontSize="small" />;
    return null;
  };

  // Obter erros para estilização dos campos
  const [errors, setErrors] = useState({
    areaLote: false,
    currentEndereco: false,
    valorInformado: false,
    matricula: false,
  });

  // Função para gerar o PDF
  // A folha é montada no backend (GET /folha/{matricula}), a partir do modelo
  // cadastrado pelo cartório e do cálculo já gravado em tb_historico_calculo.
  const generatePDF = async () => {
    if (!calculationResult) return;
    if (!matricula) {
      setError('Informe a matrícula antes de gerar a folha.');
      return;
    }

    setPdfLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${config.API_URL}/folha/${matricula}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await html2pdf()
        .from(data.html)
        .set({
          filename: data.nome_arquivo,
          // 10mm de margem em todos os lados; o CSS da folha usa 190mm de
          // largura justamente para caber em 210mm - 2x10mm sem cortar.
          margin: [10, 10, 10, 10],
          image: { type: 'jpeg', quality: 1.0 },
          html2canvas: { scale: 2, useCORS: true, windowWidth: 800, scrollX: 0, scrollY: 0 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['tr', '.data-table', '.signature', '.declaration', '.warning'],
          },
        })
        .save();

      if (!data.modelo_cadastrado) {
        setError('Folha gerada com o modelo padrão: o cartório ainda não preencheu '
          + 'o cadastro da folha (cabeçalho, CNS e UF).');
      }
    } catch (error) {
      console.error('Erro ao gerar a folha:', error);
      setError(error.response?.data?.detail || 'Erro ao gerar a folha. Tente novamente.');
    } finally {
      setPdfLoading(false);
    }
  };
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        bgcolor: 'background.default',
        p: 2,
        borderRadius: '12px',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      {/* Botão Flutuante */}
      <Fab
        color="warning"
        aria-label="reportar problema"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 40,
          zIndex: 1000,
        }}
        onClick={handleOpenIssueModal}
      >
        <ReportProblemIcon />
      </Fab>

      {/* Modal para Reportar Chamado */}
      <Dialog open={openIssueModal} onClose={handleCloseIssueModal} maxWidth="sm" fullWidth>
        <DialogTitle>Reportar Problema</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Endereço:</strong> {fullAddress || 'Não disponível'}
          </Typography>
          <TextField
            fullWidth
            label="Descrição do Problema"
            multiline
            rows={4}
            value={issueDescription}
            onChange={(e) => setIssueDescription(e.target.value)}
            variant="outlined"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIssueModal}>Cancelar</Button>
          <Button
            onClick={handleSubmitIssue}
            variant="contained"
            color="primary"
            disabled={!issueDescription.trim()}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Observações */}
      <Dialog open={openObservationsModal} onClose={handleCloseObservationsModal} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Observações</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Observações"
            multiline
            rows={4}
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseObservationsModal}>Cancelar</Button>
          <Button
            onClick={handleSaveObservations}
            variant="contained"
            color="primary"
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tópico: Dados do Cliente */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }}>
          Dados do Cliente
        </Typography>
        <IconButton color="primary" onClick={resetClientes} title="Resetar Dados do Cliente">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box>
        {clientes.map((cliente, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={cliente.nome}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(value)) {
                      handleClienteChange(index, 'nome', value);
                    }
                  }}
                  required
                  inputProps={{
                    pattern: "[a-zA-Z\\s]*",
                    title: "Apenas letras e espaços são permitidos",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPF"
                  value={cliente.cpf}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    value = value.slice(0, 11);
                    value = value.replace(/(\d{3})(\d)/, '$1.$2')
                      .replace(/(\d{3})(\d)/, '$1.$2')
                      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                    handleClienteChange(index, 'cpf', value);
                  }}
                  required
                  inputProps={{
                    pattern: '\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}',
                    maxLength: 14,
                    title: 'Digite um CPF válido no formato 123.456.789-00',
                  }}
                  placeholder="123.456.789-00"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Endereço"
                  value={cliente.endereco}
                  onChange={(e) => handleClienteChange(index, 'endereco', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={cliente.telefone}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    value = value.slice(0, 11);
                    value = value.replace(/(\d{2})(\d)/, '($1) $2');
                    handleClienteChange(index, 'telefone', value);
                  }}
                  required
                  inputProps={{
                    pattern: '\\(\\d{2}\\)\\s\\d{9}',
                    maxLength: 14,
                    title: 'Digite um telefone no formato (31) 999999999',
                  }}
                  placeholder="(31) 999999999"
                />
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                {clientes.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveCliente(index)}
                    sx={{ mr: 1 }}
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
                {index === clientes.length - 1 && (
                  <IconButton color="primary" onClick={handleAddCliente}>
                    <AddIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>

      {/* Linha Separadora */}
      <Divider sx={{ my: 2 }} />

      {/* Tópico: Matrícula */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 2 }}>
          Matrícula
        </Typography>
        <IconButton color="primary" onClick={resetMatricula} title="Resetar Matrícula">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          label="Matrícula"
          variant="outlined"
          type="number"
          value={matricula}
          onChange={(e) => {
            setMatricula(e.target.value);
            setErrors((prev) => ({ ...prev, matricula: false }));
            setShowAlert(false);
          }}
          error={errors.matricula}
          helperText={errors.matricula ? 'Campo obrigatório' : ''}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {/* Linha Separadora */}
      <Divider sx={{ my: 2 }} />

      {/* Tópico: Dados da Região */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 2 }}>
          Dados da Região
        </Typography>
        <IconButton color="primary" onClick={resetRegiao} title="Resetar Dados da Região">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={{ minWidth: 400 }}>
              <InputLabel>Cidade</InputLabel>
              <Select value={selectedCidade || ''} label="Cidade" disabled={true}>
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {filteredCidades.map((cidade) => (
                  <MenuItem key={cidade.ID_CIDADE} value={cidade.ID_CIDADE}>
                    {cidade.NOME_CIDADE}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={{ minWidth: 400 }}>
              <InputLabel>Localização</InputLabel>
              <Select value={selectedBairro || ''} label="Bairro" disabled={true}>
                <MenuItem value="">
                  <em>Selecione (opcional)</em>
                </MenuItem>
                {filteredBairros.map((bairro) => (
                  <MenuItem key={bairro.ID_BAIRRO} value={bairro.ID_BAIRRO}>
                    {bairro.NOME_BAIRRO}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" sx={{ minWidth: 400 }}>
              <InputLabel>Região</InputLabel>
              <Select
                value={currentEndereco}
                onChange={(e) => {
                  setCurrentEndereco(e.target.value);
                  setErrors((prev) => ({ ...prev, currentEndereco: false }));
                  setShowAlert(false);
                }}
                error={errors.currentEndereco}
                helperText={errors.currentEndereco ? 'Campo obrigatório' : ''}
                label="Região"
                required
                disabled={!selectedCidade}
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {enderecos.map((endereco) => (
                  <MenuItem key={endereco.ID_ENDERECO} value={endereco.ID_ENDERECO}>
                    {endereco.NOME_ENDERECO}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <TextField
            fullWidth
            label="Valor Informado Pelo Cliente"
            variant="outlined"
            value={valorInformado}
            onChange={(e) => {
              setValorInformado(formatInputCurrency(e.target.value));
              setErrors((prev) => ({ ...prev, valorInformado: false }));
              setShowAlert(false);
            }}
            error={errors.valorInformado}
            helperText={errors.valorInformado ? 'Insira um valor válido' : ''}
            sx={{ maxWidth: 400 }}
          />
        </Grid>
      </Box>

      {/* Exibir erro, se houver */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tópico: Dados do Imóvel */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', mb: 2 }}>
          Dados do Imóvel
        </Typography>
        <IconButton color="primary" onClick={resetImovel} title="Resetar Dados do Imóvel">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box>
        {addressInput && (
          <TextField
            fullWidth
            label="Endereço (rua, número, complemento)"
            variant="outlined"
            value={addressInput}
            disabled
            sx={{ mt: 2, maxWidth: 400 }}
          />
        )}
        {calculatedArea && (
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 2 }}>
            Área Calculada: {calculatedArea} m²
          </Typography>
        )}
        <Grid container spacing={1}>
          <TextField
            fullWidth
            label="Área do Terreno (m²)"
            variant="outlined"
            type="number"
            value={areaLote}
            onChange={(e) => {
              setAreaLote(e.target.value);
              setUserEdited((prev) => ({ ...prev, areaLote: true }));
              setErrors((prev) => ({ ...prev, areaLote: false }));
              setShowAlert(false);
            }}
            error={errors.areaLote}
            helperText={errors.areaLote ? 'Campo obrigatório' : ''}
            inputProps={{ min: 0, step: 'any' }}
            sx={{ mb: 2, maxWidth: 400 }}
          />
          <TextField
            fullWidth
            label="Área Construída Aproximada (m²)"
            variant="outlined"
            type="number"
            value={areaConstruida}
            onChange={(e) => {
              setAreaConstruida(e.target.value);
              setUserEdited((prev) => ({ ...prev, areaConstruida: true }));
            }}
            inputProps={{ min: 0, step: 'any' }}
            sx={{ mb: 2, maxWidth: 400 }}
          />
        </Grid>
      </Box>

      {/* Tópico: Dados da Construção */}
      {(parseFloat(areaConstruida) || 0) > 0 && metodo !== 2 ? (
        <React.Fragment>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ color: 'text.primary' }}>
              Dados da Construção
            </Typography>
            <IconButton color="primary" onClick={resetConstrucao} title="Resetar Dados da Construção">
              <RefreshIcon />
            </IconButton>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              Fator de Comercialização
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                value={selectedMercado}
                onChange={(e) => setSelectedMercado(e.target.value)}
                row
                sx={{ gap: 2 }}
              >
                {imovelData.mercado.map((item) => (
                  <FormControlLabel
                    key={item.ID}
                    value={item.ID}
                    control={<Radio />}
                    label={
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {item.DESC_TXT}
                      </Typography>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Idade Aparente (anos)"
            variant="outlined"
            type="number"
            value={idadeAparente}
            onChange={(e) => {
              setIdadeAparente(e.target.value);
            }}
            inputProps={{ min: 0 }}
            sx={{ mb: 2, maxWidth: 400 }}
          />

          {/* Tópico: Vida Útil do Imóvel */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              Vida Útil do Imóvel (Bureau Of Internal Revenue)
            </Typography>
          </Box>
          <Box>
            <FormControl fullWidth variant="outlined" sx={{ maxWidth: 400 }}>
              <InputLabel>Vida Útil</InputLabel>
              <Select
                value={selectedVidaUtil}
                onChange={(e) => setSelectedVidaUtil(e.target.value)}
                label="Vida Útil"
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {imovelData.vida_util.map((item) => (
                  <MenuItem key={item.ID} value={item.ID}>
                    {item.DESC_TXT}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tópico: Estado de Conservação do Imóvel */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              Estado de Conservação do Imóvel
            </Typography>
          </Box>
          <Box>
            <FormControl fullWidth variant="outlined" sx={{ maxWidth: 400 }}>
              <InputLabel>Estado de Conservação</InputLabel>
              <Select
                value={selectedGrauCons}
                onChange={(e) => setSelectedGrauCons(e.target.value)}
                label="Estado de Conservação"
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {imovelData.grau_cons.map((item) => (
                  <MenuItem key={item.ID} value={item.ID}>
                    {item.DESC_TXT}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tópico: Custo Unitário Básico */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              Custo Unitário Básico
            </Typography>
          </Box>
          <Box>
            <FormControl fullWidth variant="outlined" sx={{ maxWidth: 400 }}>
              <InputLabel>CUB</InputLabel>
              <Select
                value={selectedCUBC}
                onChange={(e) => setSelectedCUBC(e.target.value)}
                label="CUB"
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {imovelData.cubc.map((item) => (
                  <MenuItem key={item.ID} value={item.ID}>
                    {item.DESC_TXT}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </React.Fragment>
      ) : null}

      {/* Exibir Resultados dos Cálculos */}
      {calculationResult && (
        <Box>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
            Resultados do Cálculo:
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            ID do Cálculo: {calculationResult.id || 'N/A'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            Valor da Benfeitoria Depreciada: {formatCurrency(calculationResult.valor_benfeitoria_depreciada)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            Valor Médio Sugerido do Lote: {formatCurrency(calculationResult.valor_medio_lote)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            Valor Médio de venda Sugerido: {formatCurrency(calculationResult.valor_medio_propiedade)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            Limite Inferior de Venda: {formatCurrency(calculationResult.limite_venda_inferior)}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
            Limite Superior de Venda: {formatCurrency(calculationResult.limite_venda_superior)}
          </Typography>
          {calculationResult.valor_informado !== null && (
            <Typography
              variant="body1"
              sx={{
                color: getValorInformadoColor(
                  calculationResult.valor_informado,
                  calculationResult.limite_venda_inferior,
                  calculationResult.limite_venda_superior
                ),
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {getValorInformadoIcon(
                calculationResult.valor_informado,
                calculationResult.limite_venda_inferior,
                calculationResult.limite_venda_superior
              )}
              Valor Considerado: {formatCurrency(calculationResult.valor_informado)}
            </Typography>
          )}
          {calculationResult.clientes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                Clientes:
              </Typography>
              {calculationResult.clientes.map((cliente, index) => (
                <Box key={index} sx={{ mb: 1 }}>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    Nome: {cliente.nome}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    CPF: {cliente.cpf}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    Endereço: {cliente.endereco}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.primary' }}>
                    Telefone: {cliente.telefone}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {observations && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                Observações:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                  whiteSpace: 'pre-wrap', // Permite quebras de linha e ajusta o texto
                  wordBreak: 'break-word',
                  maxWidth: 500
                }}
              >
                {observations}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {showAlert && (
        <Alert severity="error" onClose={() => setShowAlert(false)} sx={{ maxWidth: 500 }}>
          Por favor, preencha todos os campos obrigatórios corretamente.
        </Alert>
      )}

      {/* Botões */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleCalculate}
          sx={{ maxWidth: 200 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Calcular'}
        </Button>
        {calculationResult && (
          <Button
            variant="contained"
            color="secondary"
            onClick={generatePDF}
            disabled={pdfLoading}
            startIcon={<PictureAsPdfIcon />}
            sx={{ maxWidth: 200 }}
          >
            {pdfLoading ? <CircularProgress size={24} /> : 'Gerar PDF'}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={handleOpenObservationsModal}
          startIcon={<NoteAddIcon />}
          sx={{ maxWidth: 200 }}
        >
          Observações
        </Button>
        <Button variant="outlined" onClick={onClose} sx={{ maxWidth: 200 }}>
          Voltar
        </Button>
      </Box>
    </Box>
  );
};

export default ImovelForm;